import React, { useMemo, useState } from 'react';

import { useUsers } from '../../context/users';
import { OrderType, User } from '../../context/model/types';
import FinishOrderModal from '../Modals/FinishOrderModal';
import OrderModal from '../Modals/OrderModal';
import UserModal from '../Modals/UserModal/UserModal';

// ── helpers ────────────────────────────────────────────────────────────────

const today = new Date();

const getToday = () => {
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
};

const formatSize = (size: string) => (size ? size.replace(/x/i, '×') : '—');

// ── sub-components ─────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  PENDING:    { bg: '#FEF3C7', color: '#92400E' },
  IN_TRANSIT: { bg: '#DBEAFE', color: '#1E40AF' },
  COMPLETED:  { bg: '#D1FAE5', color: '#065F46' },
  INCOMPLETE: { bg: '#FEE2E2', color: '#991B1B' },
};

const ALL_STATUSES = ['PENDING', 'IN_TRANSIT', 'INCOMPLETE', 'COMPLETED'] as const;
type Status = typeof ALL_STATUSES[number];

const StatusBadge = ({
  status,
  onClick,
}: {
  status: string;
  onClick: () => void;
}) => {
  const s = STATUS_STYLES[status] ?? { bg: '#F3F4F6', color: '#374151' };
  return (
    <span
      onClick={onClick}
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.2,
        backgroundColor: s.bg,
        color: s.color,
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      {status}
    </span>
  );
};

// ── sort helpers ────────────────────────────────────────────────────────────

type SortKey = 'status' | 'jobType' | 'date' | 'driver';

const sortOrders = (orders: OrderType[], key: SortKey, dir: 'asc' | 'desc'): OrderType[] => {
  const mult = dir === 'asc' ? 1 : -1;
  return [...orders].sort((a, b) => {
    const av = (a[key] as string) || '';
    const bv = (b[key] as string) || '';
    return mult * av.localeCompare(bv);
  });
};

// ── main component ─────────────────────────────────────────────────────────

const OrdersTable = ({
  type,
  height,
}: {
  type: 'Active Orders' | 'Closed Orders' | 'Pending Orders' | "Today's Orders" | 'All Orders';
  height?: string;
}): JSX.Element => {
  const { orders, users } = useUsers();

  const [showOrder, setShowOrder] = useState(false);
  const [showFinishOrder, setShowFinishOrder] = useState(false);
  const [orderID, setOrderID] = useState(0);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Default to showing only INCOMPLETE orders; the rest start unchecked.
  const [statusFilters, setStatusFilters] = useState<Set<Status>>(
    new Set<Status>(['INCOMPLETE'])
  );

  const showModal = (id: number, status: string) => {
    if (status === 'PENDING' || status === 'INCOMPLETE' || status === 'COMPLETED') {
      setShowOrder(true);
      setOrderID(id);
    } else if (status === 'IN_TRANSIT') {
      setShowFinishOrder(true);
      setOrderID(id);
    }
  };

  const toggleStatus = (s: Status) => {
    setStatusFilters(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const filterData = (): OrderType[] => {
    if (type === 'All Orders') return orders;
    if (type === 'Active Orders') return orders.filter(r => r.status !== 'COMPLETED');
    if (type === 'Pending Orders') return orders.filter(r => r.status === 'PENDING');
    if (type === "Today's Orders") return orders.filter(r => r.date === getToday());
    return orders.filter(r => r.status === 'COMPLETED');
  };

  const filtered = useMemo(() => {
    let result = filterData();
    if (type === 'All Orders' && statusFilters.size < ALL_STATUSES.length) {
      result = result.filter(o => statusFilters.has(o.status as Status));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(o => {
        const user = users.find(u => u.id === o.tenantID);
        return (
          (user?.name || '').toLowerCase().includes(q) ||
          String(o.id).slice(0, 5).includes(q)
        );
      });
    }
    return sortOrders(result, sortKey, sortDir);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, users, search, sortKey, sortDir, type, statusFilters]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortCaret = ({ col }: { col: SortKey }) => {
    if (col !== sortKey) return <span style={{ color: '#CCC', marginLeft: 4 }}>↕</span>;
    return <span style={{ marginLeft: 4, color: '#7B1FA2' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const thStyle = (sortable = false): React.CSSProperties => ({
    padding: '10px 12px',
    fontSize: 12,
    fontWeight: 500,
    color: '#6B7280',
    letterSpacing: 0.3,
    textAlign: 'left',
    backgroundColor: '#F9FAFB',
    borderBottom: '1px solid #E5E7EB',
    userSelect: 'none',
    cursor: sortable ? 'pointer' : 'default',
    whiteSpace: 'nowrap',
  });

  const tdStyle = (muted = false): React.CSSProperties => ({
    padding: '10px 12px',
    fontSize: 13,
    color: muted ? '#9CA3AF' : '#111827',
    verticalAlign: 'middle',
    borderBottom: '1px solid #F3F4F6',
  });

  return (
    <div
      style={{
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        // minWidth:0 lets this flex child shrink so the table's overflowX:auto
        // actually engages instead of pushing the page layout wide.
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* ── toolbar ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <p style={{ margin: 0, fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
          {filtered.length} order{filtered.length !== 1 ? 's' : ''}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {/* Status checkboxes — only shown for All Orders */}
          {type === 'All Orders' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {ALL_STATUSES.map(s => {
                const style = STATUS_STYLES[s];
                return (
                  <label
                    key={s}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', userSelect: 'none' }}
                  >
                    <input
                      type="checkbox"
                      checked={statusFilters.has(s)}
                      onChange={() => toggleStatus(s)}
                      style={{ accentColor: '#7B1FA2', cursor: 'pointer' }}
                    />
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '1px 6px',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        backgroundColor: style.bg,
                        color: style.color,
                      }}
                    >
                      {s.replace('_', ' ')}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          <input
            type="search"
            placeholder="Search tenant or order ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              padding: '6px 10px',
              borderRadius: 6,
              border: '1px solid #D1D5DB',
              fontSize: 13,
              color: '#111827',
              outline: 'none',
              width: 210,
            }}
          />
        </div>
      </div>

      {/* ── table card ── */}
      <div
        style={{
          backgroundColor: '#fff',
          border: '0.5px solid #E5E7EB',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <div style={{ overflowX: 'auto', maxHeight: height ?? '420px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th style={thStyle(true)} onClick={() => handleSort('status')}>
                  Status <SortCaret col="status" />
                </th>
                <th style={thStyle(true)} onClick={() => handleSort('jobType')}>
                  Type <SortCaret col="jobType" />
                </th>
                <th style={thStyle()}>Order ID</th>
                <th style={thStyle(true)} onClick={() => handleSort('date')}>
                  Date <SortCaret col="date" />
                </th>
                <th style={thStyle()}>Time</th>
                <th style={thStyle()}>Tenant</th>
                <th style={thStyle()}>Size</th>
                <th style={thStyle()}>Address</th>
                <th style={thStyle(true)} onClick={() => handleSort('driver')}>
                  Driver <SortCaret col="driver" />
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
                    No orders.
                  </td>
                </tr>
              )}
              {filtered.map(order => {
                const user = users.find(u => u.id === order.tenantID);
                const planSize = (user as any)?.plan?.size as string | undefined;
                return (
                  <tr
                    key={order.id}
                    style={{ backgroundColor: '#fff', transition: 'background-color 0.1s' }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F5F3FF')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#fff')}
                  >
                    <td style={tdStyle()}>
                      <StatusBadge status={order.status} onClick={() => showModal(order.id, order.status)} />
                    </td>
                    <td style={tdStyle()}>{order.jobType}</td>
                    <td style={tdStyle()}>
                      <span style={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
                        {String(order.id).slice(0, 5)}
                      </span>
                    </td>
                    <td style={tdStyle()}>{order.date}</td>
                    <td style={{ ...tdStyle(), whiteSpace: 'nowrap' }}>{order.time}</td>
                    <td
                      style={tdStyle()}
                      onClick={e => {
                        e.stopPropagation();
                        if (user) { setSelectedUser(user); setShowUserModal(true); }
                      }}
                    >
                      {user ? (
                        <span style={{ color: '#7B1FA2', textDecoration: 'underline', cursor: 'pointer' }}>
                          {user.name}
                        </span>
                      ) : (
                        <span style={{ color: '#9CA3AF' }}>{order.tenantID}</span>
                      )}
                    </td>
                    <td style={tdStyle(!planSize)}>
                      {planSize ? formatSize(planSize) : '—'}
                    </td>
                    <td style={tdStyle()}>{order.address?.addressLine1}</td>
                    <td style={tdStyle(!order.driver)}>{order.driver || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showOrder && <OrderModal setShow={setShowOrder} orderID={orderID} />}
      {showFinishOrder && <FinishOrderModal setShow={setShowFinishOrder} orderID={orderID} />}
      {showUserModal && selectedUser && <UserModal setShow={setShowUserModal} user={selectedUser} type="customer" />}
    </div>
  );
};

export default OrdersTable;
